/**
 * OCR File Processing
 * Wrapper para extrair texto de arquivos usando Google Vision
 */

import { supabase } from '@/integrations/supabase/client';

export interface ExtractResult {
  fileId: string;
  text: string;
  meta?: {
    mime: string;
    pages?: number;
    filename?: string;
    quality?: string;
    source?: string;
    structured?: any;
    [key: string]: any;
  };
}

export interface ExtractResponse {
  ok: boolean;
  items: ExtractResult[];
  error?: string;
}

/**
 * Extrair texto de arquivos usando OCR
 */
export async function extractFromFiles(
  files: File[], 
  documentId: string = 'current',
  userId?: string
): Promise<ExtractResponse> {
  try {
    console.log('📄 Iniciando extração OCR:', files.length, 'arquivos');
    
    // Validar arquivos
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      const maxSize = 20 * 1024 * 1024; // 20MB
      
      if (!validTypes.includes(file.type)) {
        console.warn('❌ Tipo de arquivo não suportado:', file.type);
        return false;
      }
      
      if (file.size > maxSize) {
        console.warn('❌ Arquivo muito grande:', file.size);
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) {
      return {
        ok: false,
        items: [],
        error: 'Nenhum arquivo válido encontrado'
      };
    }

    const items: ExtractResult[] = [];
    const errors: string[] = [];

    // Salvar arquivos no storage se userId fornecido
    if (userId) {
      for (const file of validFiles) {
        try {
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substr(2, 9);
          const filePath = `${userId}/${documentId}/${timestamp}_${randomId}_${file.name}`;
          
          const { error: uploadError } = await supabase.storage
            .from('files')
            .upload(filePath, file, {
              contentType: file.type,
              upsert: false
            });
          
          if (uploadError) {
            console.error('❌ Erro ao salvar arquivo no storage:', uploadError);
          } else {
            // Registrar no banco
            const { data: { publicUrl } } = supabase.storage
              .from('files')
              .getPublicUrl(filePath);
            
            // ✅ Usar upsert com document_id e storage_path para evitar 403/409
            const { error: metaErr } = await supabase.from('files').upsert(
              {
                id: crypto.randomUUID(),
                conversation_id: null,
                document_id: documentId,
                storage_path: filePath,
                user_id: userId,
                name: file.name,
                type: file.type,
                size: file.size,
                url: publicUrl
              }, 
              { onConflict: 'storage_path' }
            );
            
            if (metaErr) console.warn('⚠️ Falha ao registrar metadado em files:', metaErr);
            
            console.log('✅ Arquivo salvo no storage:', filePath);
          }
        } catch (storageError) {
          console.error('❌ Erro ao processar storage:', storageError);
        }
      }
    }
    
    // Processar arquivos individualmente (sequencial)
    for (const file of validFiles) {
      try {
        console.log('🔄 Processando arquivo:', file.name);
        console.log('📊 Detalhes:', {
          type: file.type,
          size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          lastModified: new Date(file.lastModified).toISOString()
        });
        
        // NOVO: Fluxo especial para PDFs
        if (file.type === 'application/pdf') {
          console.log('📄 Detectado PDF - tentando extração no cliente...');
          
          // Importação dinâmica para evitar carregar pdfjs desnecessariamente
          const { extractPdfTextClient, renderPdfPagesToImages } = await import('./pdfClient');
          
          // Passo 1: Tentar extrair texto embutido (rápido)
          const embeddedText = await extractPdfTextClient(file, 20);
          
          if (embeddedText && embeddedText.length > 50) {
            // Sucesso: PDF "digital" com texto embutido
            console.log(`✅ Texto extraído do PDF (${embeddedText.length} caracteres)`);
            items.push({
              fileId: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              text: embeddedText,
              meta: {
                mime: file.type,
                filename: file.name,
                quality: 'high_native_text',
                source: 'pdf_embedded_text'
              }
            });
            continue;
          }
          
          // Passo 2: PDF escaneado - renderizar páginas como imagens
          console.log('📸 PDF sem texto embutido - renderizando páginas como imagens...');
          
          try {
            const pagesImages = await renderPdfPagesToImages(file, {
              maxPages: 10,
              scale: 2.0
            });
            
            console.log(`✅ ${pagesImages.length} páginas renderizadas`);
            
            // Processar cada página via Google Vision
            let ocrText = '';
            for (let pageIdx = 0; pageIdx < pagesImages.length; pageIdx++) {
              console.log(`🔍 OCR na página ${pageIdx + 1}/${pagesImages.length}...`);
              
              const { data, error } = await supabase.functions.invoke('google-vision', {
                body: {
                  imageBase64: pagesImages[pageIdx],
                  mime: 'image/png',
                  filename: `${file.name}#page${pageIdx + 1}`,
                  documentId
                }
              });
              
              if (error) {
                console.warn(`⚠️ Erro OCR na página ${pageIdx + 1}:`, error);
                continue; // Tentar próxima página
              }
              
              const pageText = (data?.text || '').trim();
              if (pageText) {
                ocrText += (ocrText ? `\n\n--- Página ${pageIdx + 1} ---\n\n` : '') + pageText;
              }
            }
            
            if (!ocrText) {
              throw new Error('Nenhum texto foi detectado nas páginas do PDF');
            }
            
            items.push({
              fileId: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              text: ocrText,
              meta: {
                mime: file.type,
                filename: file.name,
                quality: 'ocr_from_rendered_pages',
                pages: pagesImages.length
              }
            });
            
            console.log(`✅ OCR concluído: ${ocrText.length} caracteres`);
            
          } catch (renderError) {
            throw new Error(`Falha ao processar PDF escaneado: ${renderError instanceof Error ? renderError.message : 'Erro desconhecido'}`);
          }
          
          continue;
        }
        
        // IMAGENS: fluxo original (inalterado)
        const imageBase64 = await fileToBase64(file);
        console.log(`✅ Base64 gerado: ${imageBase64.length} caracteres`);
        
        // Chamar Edge Function google-vision com payload correto
        const { data, error } = await supabase.functions.invoke('google-vision', {
          body: {
            imageBase64,
            mime: file.type,
            filename: file.name,
            documentId
          }
        });

        if (error) {
          console.error('❌ Erro na extração OCR para', file.name, ':', error);
          
          // Extrair mensagem mais detalhada
          const errorMsg = error.message || error.msg || 'Erro na extração';
          
          // Mensagens específicas para usuário
          let userFriendlyMsg = errorMsg;
          if (errorMsg.includes('PDF') || errorMsg.includes('pdf')) {
            userFriendlyMsg = 'Erro ao processar PDF. Verifique se o arquivo não está corrompido ou protegido.';
          } else if (errorMsg.includes('Sistema de análise não configurado') || errorMsg.includes('Configure')) {
            userFriendlyMsg = 'API do Google Vision não configurada. Configure nas Configurações do Sistema.';
          } else if (errorMsg.includes('Nenhum texto foi detectado')) {
            userFriendlyMsg = 'Nenhum texto foi detectado no documento. Tente com uma imagem de melhor qualidade.';
          }
          
          errors.push(`${file.name}: ${userFriendlyMsg}`);
          
          // Exibir toast para usuário (importação dinâmica)
          try {
            const { toast } = await import('sonner');
            toast.error(`Erro ao processar ${file.name}`, {
              description: userFriendlyMsg
            });
          } catch {
            // Ignorar erro de toast se não estiver disponível
          }
          
          continue;
        }

        // Mapear resposta da Edge Function
        const { text, structured_data, extraction_quality, meta } = data || {};
        
        items.push({
          fileId: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: text || '',
          meta: {
            mime: file.type,
            filename: file.name,
            quality: extraction_quality,
            structured: structured_data,
            ...(meta || {})
          }
        });

        console.log('✅ OCR concluído para:', file.name, `(${text?.length || 0} caracteres)`);

      } catch (fileError) {
        console.error('❌ Erro ao processar arquivo', file.name, ':', fileError);
        errors.push(`${file.name}: ${fileError instanceof Error ? fileError.message : 'Erro desconhecido'}`);
      }
    }

    console.log('🎯 Extração finalizada:', items.length, 'sucessos,', errors.length, 'erros');
    
    return {
      ok: items.length > 0,
      items,
      error: errors.length > 0 ? errors.join('; ') : undefined
    };

  } catch (error) {
    console.error('❌ Erro geral ao processar arquivos:', error);
    return {
      ok: false,
      items: [],
      error: error instanceof Error ? error.message : 'Erro desconhecido'
    };
  }
}

/**
 * Converter arquivo para base64
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remover o prefixo "data:type;base64," se existir
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}